/* eslint-disable @tanstack/query/exhaustive-deps -- doesn't work with the key factory*/
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "~/providers/store/auth";

import { getSchools } from "../../schools/requests";
import { getTills } from "../requests";
import { tillsKeys } from "../keys";
import { type Till } from "../types";

export function useTills() {
  const { partnerId, schoolId, moduleId, tillId } = useAuth();

  return useQuery({
    queryKey: tillsKeys.list({
      filters: { assigned: true }
    }),
    queryFn: () =>
      getTills({
        partnerId: partnerId ?? undefined,
        schoolId: schoolId ?? undefined,
        moduleId: moduleId ?? undefined,
        assigned: true
      }),
    select: (tills) => {
      if (!tillId) {
        return tills;
      }

      // Move the current till to the beginning of the array
      return [
        ...tills.filter((till) => till.id === tillId),
        ...tills.filter((till) => till.id !== tillId)
      ];
    }
  });
}

export function useUnassignedTillsBySchool() {
  const { partnerId } = useAuth();

  return useQuery({
    queryKey: tillsKeys.list({
      filters: { assigned: false }
    }),
    queryFn: async () => {
      if (!partnerId) {
        throw new Error("No partner found");
      }

      const [tills, schools] = await Promise.all([
        getTills({
          partnerId,
          assigned: false
        }),
        getSchools({
          partnerId
        })
      ]);

      return schools.reduce<
        {
          schoolId: string;
          name: string;
          tills: Pick<Till, "id" | "name" | "type">[];
        }[]
      >((acc, school) => {
        const schoolTills = tills.filter((till) => till.schoolId === school.id);

        if (schoolTills.length === 0) {
          return acc;
        }

        return [
          ...acc,
          {
            schoolId: school.id,
            name: school.name,
            tills: schoolTills
          }
        ];
      }, []);
    },
    enabled: Boolean(partnerId)
  });
}
